import { useEffect, useState } from 'react';
import './EditTask.css';
import Modal from '../../component/modal/Modal';
import { TaskReward } from '../../api/models/task/taskreward';
import { TaskType } from '../../api/models/task/taskmodel';
import RadioButtons from '../../component/radio/RadioButtons';
import { ChanceSlider } from '../chance/ChanceSlider';

type EditTaskProps = {
    task: any;
    save: (task: any) => Promise<boolean>;
    del: (task: any) => Promise<boolean>;
    clearCompletions: (task: any) => Promise<boolean>;
    close: () => void;
};

const EditTaskDialog = ({ task, save, del, clearCompletions, close }: EditTaskProps) => {
    const [name, setName] = useState<string>(task.name ?? "");
    const [description, setDescription] = useState<string>(task.description ?? "");
    const [emoji, setEmoji] = useState<string>(task.emoji ?? "");
    const [reward, setReward] = useState<TaskReward>(task.reward ?? TaskReward.createGuaranteed(5));
    const [rewardType, setRewardType] = useState<string>(reward.getChance === 1 ? "Always" : "Chance");
    const [timeLimit, setTimeLimit] = useState<number | undefined>(task.timeLimit);
    const [type, setType] = useState<TaskType>(task.type ?? TaskType.OneOff);
    const [minimumPerWeek, setMinimumPerWeek] = useState<number | undefined>(task.minimumPerWeek);
    const [maximumPerWeek, setMaximumPerWeek] = useState<number | undefined>(task.maximumPerWeek);

    useEffect(() => {
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const toLocalDateTimeString = (timestamp: number) => {
        const date = new Date(timestamp);
        const tzOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
        const localISOTime = new Date(date.getTime() - tzOffset).toISOString().slice(0, -1);
        return localISOTime.slice(0, 16); // return 'YYYY-MM-DDTHH:MM'
    };

    const promptDelete = async () => {
        if (window.confirm(`Are you sure you want to delete ${task.name}?`)) {
            if (await del(task)) {
                close();
            }
        }
    }

    const promptClearCompletions = async () => {
        if (window.confirm(`Are you sure you want to clear completions of ${task.name}?`)) {
            if (await clearCompletions(task)) {
                close();
            }
        }
    }

    const changeTaskType = (newType: string) => {
        setType(newType as TaskType);

        if (newType === TaskType.OneOff) {
            setMinimumPerWeek(-1);
            setMaximumPerWeek(-1);
            setTimeLimit(task.timeLimit ?? undefined);
        } else {
            setTimeLimit(undefined);
            setMinimumPerWeek(0);
            setMaximumPerWeek(0);
        }
    }

    const changeRewardType = (newType: string) => {
        setRewardType(newType);
        if (newType === "Always") {
            setReward(TaskReward.createGuaranteed(reward.points));
        } else {
            setReward(TaskReward.createChance(reward.points, 0.2, 0.2, 1.5, 0.2, 0.2, 0.2, 1.5));
        }
    };

    const updateChance = (field: string, value: number) => {
        const newValue = Number.isNaN(value) ? 0 : Math.max(0, Math.min(value, 1));

        const newChances = Object.keys(reward).filter(key => key.includes("Chance")).reduce((acc, key) => {
            acc[key] = reward[key as keyof TaskReward];
            return acc;
        }, {} as Record<string, number>);
        newChances[field] = newValue;

        let remainingTotal = 1 - newValue;

        const otherFields = Object.keys(newChances).filter(key => key !== field);

        let totalOtherChances = otherFields.reduce((acc, key) => acc + reward[key as keyof TaskReward], 0);

        otherFields.forEach(key => {
            if (totalOtherChances === 0) {
                // Avoid division by zero if all other values are zero
                newChances[key] = remainingTotal / otherFields.length;
            } else {
                const proportionalAdjustment = (newChances[key] / totalOtherChances) * remainingTotal;
                newChances[key] = Math.max(0, proportionalAdjustment);
            }
        });

        // Ensure that the total sums up to exactly 1 after adjustments (due to rounding errors)
        const finalTotal = Object.values(newChances).reduce((acc, chance) => acc + chance, 0);
        const adjustment = 1 - finalTotal;

        if (Math.abs(adjustment) > 0) {
            newChances[otherFields[0]] = Math.max(0, newChances[otherFields[0]] + adjustment);
        }
        setReward(TaskReward.copyWith(reward, newChances));
    }

    const isCreation = task.id === undefined;

    return (
        <Modal isOpen>
            <div id="edit-task">
                <h2>{isCreation ? "Create" : "Edit"} Task</h2>
                {!isCreation && <>
                    <span className="editing">Editing: {task.name}</span>
                    <button className="small destructive" onClick={promptClearCompletions}>Reset completions</button>
                    <button className="small destructive" onClick={promptDelete}>Delete</button>
                </>
                }
                <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (await save({ id: !isCreation ? task.id : undefined, name, description, emoji, type, reward, timeLimit, minimumPerWeek, maximumPerWeek })) {
                        close();
                    }
                }}>
                    <label>
                        Name*:
                        <input type="text" value={name} onChange={e => setName(e.target.value)} required />
                    </label>
                    <label>
                        Description:
                        <textarea value={description} onChange={e => setDescription(e.target.value)} />
                    </label>
                    <label>
                        Emoji:
                        <input type="text" value={emoji} onChange={e => setEmoji(e.target.value)} />
                    </label>
                    <div className="task-type">
                        <p><b>Task type:</b></p>
                        <RadioButtons name="task-type" values={[TaskType.OneOff, TaskType.Repeatable]} selected={type} onChange={changeTaskType} />
                    </div>
                    {type === TaskType.OneOff && <>
                        <label>
                            <span>Time limit: <button type="button" className="clear secondary small" onClick={() => setTimeLimit(undefined)}>Clear</button></span>
                            <input type="datetime-local" value={timeLimit ? toLocalDateTimeString(timeLimit) : ""} onChange={e => setTimeLimit(new Date(e.target.value).getTime())} />
                        </label>
                    </>
                    }
                    {type === TaskType.Repeatable && <>
                        <label>
                            Minimum per week (0 for no minimum):
                            <input type="number" value={minimumPerWeek ?? 0} onChange={e => setMinimumPerWeek(Number(e.target.value))} />
                        </label>
                        <label>
                            Maximum per week (0 for no maximum):
                            <input type="number" value={maximumPerWeek ?? 0} onChange={e => setMaximumPerWeek(Number(e.target.value))} />
                        </label>
                    </>}
                    <h3>Reward</h3>
                    <label>
                        Points:
                        <input type="number" value={reward.points} onChange={e => setReward(TaskReward.copyWith(reward, { points: Number(e.target.value) }))} />
                    </label>
                    <div className="reward-type">
                        <p><b>Reward type:</b></p>
                        <RadioButtons name="task-type" values={["Always", "Chance"]} selected={rewardType} onChange={changeRewardType} />
                    </div>
                    {rewardType === "Chance" && <>
                        <label>
                            Multiplier chance:
                            <ChanceSlider chance={reward.multiplierChance} onChange={value => updateChance("multiplierChance", value)} />
                        </label>
                        <label>
                            Win chance:
                            <ChanceSlider chance={reward.getChance} onChange={value => updateChance("getChance", value)} />
                        </label>
                        <label>
                            Nothing chance:
                            <ChanceSlider chance={reward.nothingChance} onChange={value => updateChance("nothingChance", value)} />
                        </label>
                        <label>
                            Loss chance:
                            <ChanceSlider chance={reward.removeChance} onChange={value => updateChance("removeChance", value)} />
                        </label>
                        <label>
                            Multiplier loss chance:
                            <ChanceSlider chance={reward.removeMultiplierChance} onChange={value => updateChance("removeMultiplierChance", value)} />
                        </label>
                        <label>
                            Multiplier:
                            <input type="number" value={reward.multiplier} onChange={e => setReward(TaskReward.copyWith(reward, { multiplier: Number(e.target.value) }))} />
                        </label>
                        <label>
                            Loss multiplier:
                            <input type="number" value={reward.removeMultiplier} onChange={e => setReward(TaskReward.copyWith(reward, { removeMultiplier: Number(e.target.value) }))} />
                        </label>
                    </>}
                    <div className="actions">
                        <button type="button" className="secondary" onClick={e => { e.stopPropagation(); close() }}>Cancel</button>
                        <button type="submit">Save</button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default EditTaskDialog;